import React, { useEffect, useState } from 'react';
import {
  Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Radio, RadioGroup,
  TextField, Typography,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useUserState } from '../context/UserContext';
import { SubjectActionTypes, useSubjectDispatch } from '../context/SubjectContext';
import { useStudy } from '../context/StudyContext';
import { getSubjectDetails } from '../common/apiGetUtilities';
import {
  ClaimCandidate, ClaimPreview, ClaimResult, StudyApiError, looksLikeClaimCode, normalizeClaimCode, previewClaim,
  redeemClaim, requestEnrollmentResend,
} from '../common/apiStudyUtilities';
import { showToast } from '../common/utilities';

export type ClaimReason = 'registration' | 'banner' | 'upload' | 'settings' | 'link';

type Props = {
  open: boolean;
  reason: ClaimReason;
  initialCode?: string;
  /** Called on every close. `result` is set when a child was linked. */
  onClose: (result?: ClaimResult) => void;
};

type Step = 'enter' | 'preview' | 'pick' | 'done';

/**
 * Preview-then-confirm redemption of an enrollment claim code (cribsy-api DECISIONS.md D15; app D18-D19).
 * Enter the code → see "Link Ava, born March 2026?" → confirm → "Linked: Ava, CR6015". Never blocks anything:
 * every step has a way out, and the caller decides what happens next (for the upload gate, the upload proceeds).
 */
export default function StudyClaimDialog({ open, reason, initialCode, onClose }: Props) {
  const { t, i18n } = useTranslation();
  const user = useUserState();
  const subjectDispatch = useSubjectDispatch();
  const study = useStudy();

  const [step, setStep] = useState<Step>('enter');
  const [code, setCode] = useState('');
  const [preview, setPreview] = useState<ClaimPreview | null>(null);
  const [candidates, setCandidates] = useState<ClaimCandidate[]>([]);
  const [chosenSubject, setChosenSubject] = useState('');
  const [result, setResult] = useState<ClaimResult | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStep('enter'); setPreview(null); setCandidates([]); setChosenSubject(''); setResult(null); setError(''); setBusy(false);
    setCode(normalizeClaimCode(initialCode));
  }, [open, initialCode]);

  const tr = (key: string, opts?: any): string => String(t(key, { ns: 'study', ...opts }));

  const describeError = (err: any): string => {
    const code = err instanceof StudyApiError ? err.code : 'other';
    return tr(`err_${code}`);
  };

  const birthMonthLabel = (ym?: string): string => {
    if (!ym) return '';
    const [y, m] = ym.split('-').map(Number);
    if (!y || !m) return ym;
    return new Date(y, m - 1, 1).toLocaleDateString(i18n.language, { month: 'long', year: 'numeric' });
  };

  const doPreview = async () => {
    const normalized = normalizeClaimCode(code);
    setCode(normalized);
    if (!looksLikeClaimCode(normalized)) { setError(tr('err_badcode')); return; }
    setBusy(true); setError('');
    try {
      const p = await previewClaim(user.firebaseUser, normalized);
      setPreview(p);
      setStep('preview');
    } catch (err) {
      setError(describeError(err));
    } finally {
      setBusy(false);
    }
  };

  const doClaim = async (subjectId?: string) => {
    setBusy(true); setError('');
    try {
      const r = await redeemClaim(user.firebaseUser, code, subjectId);
      setResult(r);
      setStep('done');
      await study.refresh();
      // the claim may have created or changed the subject; reload it so the dashboard sees the link at once
      if (user.firebaseUser && user._id) {
        const subject = await getSubjectDetails(user.firebaseUser, user._id);
        if (subject) subjectDispatch({ type: SubjectActionTypes.UPDATE, ...subject });
      }
      study.setPendingCode(undefined);
    } catch (err) {
      if (err instanceof StudyApiError && err.code === 'candidates' && err.candidates?.length) {
        setCandidates(err.candidates);
        setChosenSubject(err.candidates[0].subjectId);
        setStep('pick');
      } else {
        setError(describeError(err));
        if (err instanceof StudyApiError && ['gone', 'locked', 'conflict'].includes(err.code)) setStep('enter');
      }
    } finally {
      setBusy(false);
    }
  };

  const doResend = async () => {
    setBusy(true);
    const ok = await requestEnrollmentResend(user.firebaseUser);
    setBusy(false);
    showToast(tr(ok ? 'resent' : 'resendfailed'));
  };

  const close = () => {
    if (result) onClose(result);
    else { if (reason === 'link') study.setPendingCode(undefined); onClose(undefined); }
  };

  const dismissLabel = reason === 'upload' ? tr('notnow') : reason === 'registration' ? tr('skip') : tr('cancel');

  return (
    <Dialog open={open} onClose={busy ? undefined : close} fullWidth maxWidth="xs" aria-labelledby="study-claim-title">
      <DialogTitle id="study-claim-title">{tr('dialogtitle')}</DialogTitle>
      <DialogContent>
        {step === 'enter' && (
          <>
            <Typography variant="body2" gutterBottom>{tr(`intro_${reason}`)}</Typography>
            <TextField
              autoFocus fullWidth margin="normal" variant="outlined"
              id="claim-code" label={tr('codelabel')} placeholder={tr('codeplaceholder')}
              value={code} onChange={e => { setCode(e.target.value.toUpperCase()); setError(''); }}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); doPreview(); } }}
              inputProps={{ autoCapitalize: 'characters', autoCorrect: 'off', spellCheck: false, style: { letterSpacing: 1 } }}
              error={!!error} helperText={error || ' '}
              disabled={busy}
            />
            {study.pending && (
              <Button size="small" onClick={doResend} disabled={busy}>{tr('resend')}</Button>
            )}
          </>
        )}
        {step === 'preview' && preview && (
          <>
            <Typography variant="h6" gutterBottom>
              {tr('previewtitle', { name: preview.child.firstName, month: birthMonthLabel(preview.child.birthMonth) })}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {preview.alreadyClaimedByYou ? tr('alreadyyours') : preview.claimedByAnother ? tr('err_conflict') : tr('previewhelp')}
            </Typography>
            {error && <Typography variant="body2" color="error">{error}</Typography>}
          </>
        )}
        {step === 'pick' && (
          <>
            <Typography variant="body1" gutterBottom>{tr('picktitle')}</Typography>
            <RadioGroup value={chosenSubject} onChange={e => setChosenSubject(e.target.value)}>
              {candidates.map(c => (
                <FormControlLabel key={c.subjectId} value={c.subjectId} control={<Radio />}
                  label={`${c.name || tr('unnamedchild')}${c.dob ? ` · ${new Date(c.dob).toLocaleDateString(i18n.language)}` : ''}`} />
              ))}
            </RadioGroup>
            {error && <Typography variant="body2" color="error">{error}</Typography>}
          </>
        )}
        {step === 'done' && result && (
          <>
            <Typography variant="h6" gutterBottom>{tr('donetitle', { name: result.child.firstName, studyid: result.studyid })}</Typography>
            {!!result.assessmentsLinked && (
              <Typography variant="body2">{tr('donebackfill', { count: result.assessmentsLinked })}</Typography>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {busy && <CircularProgress size={20} style={{ marginRight: 8 }} />}
        {step === 'enter' && (
          <>
            <Button onClick={close} disabled={busy}>{dismissLabel}</Button>
            <Button onClick={doPreview} color="primary" variant="contained" disabled={busy || code.length < 4}>{tr('continue')}</Button>
          </>
        )}
        {step === 'preview' && (
          <>
            <Button onClick={() => { setStep('enter'); setError(''); }} disabled={busy}>{tr('back')}</Button>
            <Button onClick={() => doClaim()} color="primary" variant="contained" disabled={busy || !!preview?.claimedByAnother}>
              {preview?.alreadyClaimedByYou ? tr('done') : tr('link')}
            </Button>
          </>
        )}
        {step === 'pick' && (
          <>
            <Button onClick={() => setStep('preview')} disabled={busy}>{tr('back')}</Button>
            <Button onClick={() => doClaim(chosenSubject)} color="primary" variant="contained" disabled={busy || !chosenSubject}>{tr('link')}</Button>
          </>
        )}
        {step === 'done' && (
          <Button onClick={close} color="primary" variant="contained">{tr('done')}</Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
